import DOMPurify from 'dompurify';

import {
    appendEscapeParam,
    encodePathSegment,
    formatDateToMedium,
    restUrl,
} from '@/services/utils';

export type ReviewStatusBadgeVariant = 'info' | 'success' | 'error' | 'warning' | 'neutral';

export function reviewStatusBadgeVariant(status?: string): ReviewStatusBadgeVariant {
    switch (status) {
        case 'APPROVED':
            return 'success';
        case 'REJECTED':
            return 'error';
        case 'REVIEWED':
            return 'warning';
        case 'RECEIVED':
            return 'info';
        default:
            return 'neutral';
    }
}

export const VIEW_BY_REVIEWER = 'ByReviewer';
export const VIEW_BY_ERRORS_WARNINGS = 'ByErrorsWarnings';
export const REVIEW_MESSAGE_MAX_LENGTH = 150;
export const REVIEW_DESCRIPTION_LINES = 2;
/** Used when the review column width cannot be measured. Two lines of this column are about this long. */
export const REVIEW_TWO_LINE_FALLBACK_CHARS = 160;
const REVIEW_MESSAGE_PROBE_LENGTH = 60;
export const GLN_PATTERN_LENGTH = 13;

const REVIEW_ENTITY_ATTRIBUTES = [
    'channelId',
    'combinedComment',
    'comment',
    'customValues',
    'date',
    'messagingCreatorType',
    'primaryKey',
    'reviewStatus',
    'reviewer',
    'supplier',
    'supplierId',
    'reviewer__',
] as const;

export interface ReviewerRef {
    id?: string;
    name?: string;
}

export interface SupplierReview {
    channelId?: string;
    combinedComment?: string;
    comment?: string;
    generatedComment?: string;
    customValues?: Record<string, unknown>;
    date?: string | number;
    messagingCreatorType?: string;
    primaryKey?: string;
    reviewStatus?: string;
    reviewer?: string;
    supplier?: string;
    supplierId?: string | number;
    reviewerId?: string | number;
    reviewId?: string;
    reviewErrors?: string[];
    reviewWarnings?: string[];
    reviewRemarks?: string[];
    reviewer__?: ReviewerRef;
}

export interface OrganizationContact {
    organizationId?: string | number;
    name?: string;
}

export interface CommunicationChannel {
    subDestinations?: Record<string, string>;
}

export interface GdsnParty {
    gln?: string;
    name?: string;
}

export function supplierReviewsUrl(
    primaryKey: string,
    filters: { publicationDestination?: string; reviewer?: string },
): string {
    const encoded = encodePathSegment(primaryKey, '~');
    const params = new URLSearchParams();
    if (filters.publicationDestination != null && filters.publicationDestination !== '') {
        params.set('publicationDestination', filters.publicationDestination);
    }
    if (filters.reviewer != null && filters.reviewer !== '') {
        params.set('reviewer', filters.reviewer);
    }
    const query = params.toString();
    const url = restUrl(`supplierreviews/${encoded}`) + (query ? `?${query}` : '');
    return appendEscapeParam(url, '~', primaryKey);
}

export function attachReviewer(
    review: SupplierReview,
    organizations: OrganizationContact[],
    channels: CommunicationChannel[],
): SupplierReview {
    const reviewer__: ReviewerRef = {};

    if (review.reviewerId != null) {
        reviewer__.id = review.reviewId;
        const organization = organizations.find(
            (entry) => String(entry.organizationId) === String(review.reviewerId),
        );
        if (organization?.name) {
            reviewer__.name = organization.name;
        }
    } else {
        reviewer__.id = review.reviewer;
        const channel = channels.find((entry) =>
            review.reviewer != null
            && entry.subDestinations != null
            && entry.subDestinations[review.reviewer] != null,
        );
        if (channel && review.reviewer) {
            reviewer__.name = channel.subDestinations?.[review.reviewer];
        }
    }

    return { ...review, reviewer__ };
}

export function gdsnIdsNeedingLookup(reviews: SupplierReview[]): string[] {
    const ids = new Set<string>();
    reviews.forEach((review) => {
        const id = review.reviewer__?.id;
        if (!review.reviewer__?.name && id && id.length === GLN_PATTERN_LENGTH) {
            ids.add(id);
        }
    });
    return [...ids];
}

export function indexGdsnParties(parties: GdsnParty[]): Record<string, GdsnParty> {
    return parties.reduce<Record<string, GdsnParty>>((indexed, party) => {
        if (party.gln) {
            indexed[party.gln] = party;
        }
        return indexed;
    }, {});
}

export function reviewerName(
    review: SupplierReview,
    gdsnParties: Record<string, GdsnParty>,
): string | null {
    if (review.reviewer__?.name) {
        return review.reviewer__.name;
    }
    const partyName = review.reviewer__?.id ? gdsnParties[review.reviewer__.id]?.name : undefined;
    return partyName || null;
}

export function groupMessages(
    reviews: SupplierReview[],
    field: 'reviewErrors' | 'reviewWarnings',
): Record<string, SupplierReview[]> {
    const groups: Record<string, SupplierReview[]> = {};
    reviews.forEach((review) => {
        const messages = review[field];
        if (!messages) {
            return;
        }
        messages.forEach((message) => {
            const sanitized = Object.fromEntries(
                REVIEW_ENTITY_ATTRIBUTES
                    .filter((key) => review[key] !== undefined)
                    .map((key) => [key, review[key]]),
            ) as SupplierReview;
            if (groups[message]) {
                groups[message].push(sanitized);
            } else {
                groups[message] = [sanitized];
            }
        });
    });
    return groups;
}

export function showsErrorGroups(showErrors: boolean, showWarnings: boolean): boolean {
    return showErrors || (!showErrors && !showWarnings);
}

export function showsWarningGroups(showErrors: boolean, showWarnings: boolean): boolean {
    return showWarnings || (!showErrors && !showWarnings);
}

export function aggregatedComments(reviews: SupplierReview[]): SupplierReview[] {
    return reviews.filter((review) =>
        review.combinedComment || review.comment || review.generatedComment,
    );
}

export function sanitizeReviewHtml(html?: string): string {
    if (!html) {
        return '';
    }
    return DOMPurify.sanitize(html);
}

export function stripHtml(html: string): string {
    if (!html || typeof html !== 'string') {
        return html;
    }
    const text = new DOMParser().parseFromString(sanitizeReviewHtml(html), 'text/html').body.textContent || '';
    return text.replace(/\s+/g, ' ').trim();
}

export function extractPlainSegments(html?: string): string[] {
    if (!html) {
        return [];
    }
    const withBreaks = html
        .replace(/<\s*(hr|br|\/p)\s*\/?>/gi, '\n')
        .replace(/<[^>]+>/g, '');
    const textarea = document.createElement('textarea');
    textarea.innerHTML = withBreaks;
    return textarea.value
        .split('\n')
        .map((segment) => segment.replace(/^EN:\s*/i, '').trim())
        .filter((segment) => segment.length > 0);
}

export function reviewMessageKey(review: SupplierReview, type: string, index: number): string {
    const reviewerId = review.reviewer__?.id ?? review.reviewer;
    return `${review.primaryKey}:${reviewerId}:${type}:${index}`;
}

export function isReviewMessageLong(message?: string): boolean {
    return Boolean(message && message.length > REVIEW_MESSAGE_MAX_LENGTH);
}

function normalizeReviewText(value?: string): string {
    if (!value || typeof value !== 'string') {
        return '';
    }
    return stripHtml(value).replace(/^EN:\s*/i, '').trim();
}

function normalizedReviewSegments(html?: string): string[] {
    return extractPlainSegments(html)
        .map((segment) => segment.replace(/\s+/g, ' ').trim())
        .filter((segment) => segment.length > 0);
}

function longestSegment(segments: string[], accept: (segment: string) => boolean): string {
    let best = '';
    for (const segment of segments) {
        if (accept(segment) && segment.length > best.length) {
            best = segment;
        }
    }
    return best;
}

export function getFullReviewMessage(review: SupplierReview, shortMessage: string): string {
    const plainShort = normalizeReviewText(shortMessage);
    if (!plainShort || !review.combinedComment) {
        return plainShort;
    }

    const segments = normalizedReviewSegments(review.combinedComment);
    const fullPlain = segments.join(' ');
    const longerPrefix = longestSegment(segments, (segment) =>
        segment.startsWith(plainShort) && segment.length > plainShort.length);
    if (longerPrefix) {
        return longerPrefix;
    }

    // The stored value spans several comment lines, and the comment continues past it.
    if (fullPlain.startsWith(plainShort) &&
        fullPlain.length > plainShort.length &&
        !segments.some((segment) => segment === plainShort)) {
        return fullPlain;
    }

    const probe = plainShort.slice(0, Math.min(REVIEW_MESSAGE_PROBE_LENGTH, plainShort.length));
    const containing = longestSegment(segments, (segment) =>
        segment.includes(probe) && segment.length > plainShort.length);
    if (containing) {
        return containing;
    }

    if (fullPlain.includes(probe) &&
        fullPlain.length > plainShort.length &&
        !segments.some((segment) => segment === plainShort)) {
        return fullPlain;
    }

    return plainShort;
}

export function descriptionExceedsTwoLines(text?: string): boolean {
    if (!text || !text.trim()) {
        return false;
    }
    const withBreaks = text
        .replace(/<\s*(hr|br|\/p|\/div|\/li)\s*\/?>/gi, '\n')
        .replace(/<[^>]+>/g, '');
    const lines = withBreaks
        .split('\n')
        .map((line) => line.replace(/\s+/g, ' ').trim())
        .filter((line) => line.length > 0);
    if (lines.length > REVIEW_DESCRIPTION_LINES) {
        return true;
    }
    return lines.join(' ').length > REVIEW_TWO_LINE_FALLBACK_CHARS;
}

export function needsReviewMessageExpand(review: SupplierReview, shortMessage: string): boolean {
    return descriptionExceedsTwoLines(getFullReviewMessage(review, shortMessage));
}

export function formatCustomValue(value: unknown): string {
    if (value == null) {
        return '';
    }
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    return String(value);
}

export function formatReviewDate(date?: string | number): string {
    if (date == null || date === '') {
        return '';
    }
    return formatDateToMedium(date);
}
